import React, { useState, useEffect } from 'react';
import { Plus, Search, DollarSign, Clock, TrendingUp, TrendingDown, Mail, Info, FileText, Link, CheckCheck, CreditCard, MoreVertical, Send, RefreshCw, Check, Calendar, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { invoicesApi, jobsApi } from '../services/api';
import api from '../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import PaymentReconciliation from '../components/PaymentReconciliation';
import ContractBillingDue from '../components/ContractBillingDue';
import { InvoiceDetailDrawer, InvoiceListItem, PaymentsTab } from '../components/invoices';

const Invoices = () => {
  const navigate = useNavigate();
  const { user } = useBusinessContext();
  const isOwner = user?.role === 'Owner';

  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Contract billing state
  const [contractCustomersDue, setContractCustomersDue] = useState([]);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  const [payingInvoice, setPayingInvoice] = useState(null);
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'danger',
    onConfirm: () => {},
  });

  // Reconciliation tab state
  const [activeTab, setActiveTab] = useState('invoices');
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);

  // Add state for drawer
const [selectedInvoice, setSelectedInvoice] = useState(null);
const [drawerOpen, setDrawerOpen] = useState(false);

// Handle invoice click
const handleInvoiceClick = (invoice) => {
  setSelectedInvoice(invoice);
  setDrawerOpen(true);
};


  // Business settings state (email + tax)
  const [businessSettings, setBusinessSettings] = useState({
    sendInvoiceCreated: false,
    sendPaymentReceived: false,
    sendPaymentReminders: false,
    collectsSalesTax: false,
    salesTaxRate: 0,
    taxSetupComplete: false,
    state: null,
    county: null,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Email override state for forms
  const [emailOverrides, setEmailOverrides] = useState({
    sendInvoiceEmail: false,
    sendReceiptEmail: false,
  });

  const [generatingInvoiceFor, setGeneratingInvoiceFor] = useState(null);

  // Tax override for "quote includes tax" scenarios
  const [taxIncludedInQuote, setTaxIncludedInQuote] = useState(false);

  const [formData, setFormData] = useState({
    jobId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    subTotal: '',
    taxAmount: '',
    totalAmount: '',
    notes: '',
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'Cash',
    paymentDate: new Date().toISOString().split('T')[0],
    transactionId: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
    loadBusinessSettings();
    loadPendingPaymentCount();
  }, []);

  useEffect(() => {
    let filtered = invoices;

    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.job?.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'unpaid') {
        filtered = filtered.filter(invoice => 
          ['Pending', 'Unpaid', 'Partial'].includes(invoice.status)
        );
      } else {
        filtered = filtered.filter(invoice => 
          invoice.status.toLowerCase() === statusFilter.toLowerCase()
        );
      }
    }

    setFilteredInvoices(filtered);
  }, [searchTerm, statusFilter, invoices]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenu(null);
    if (openActionMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenu]);

  const loadData = async () => {
      try {
        setLoading(true);
        const [invoicesRes, jobsRes] = await Promise.all([
          invoicesApi.getAll(),
          jobsApi.getAll(),
        ]);
        
        setInvoices(invoicesRes.data);
        setFilteredInvoices(invoicesRes.data);
        
        // Filter jobs for "Ready to Invoice" - exclude contract billing customers
        const completedJobs = jobsRes.data.filter(j => j.status === 'Completed' && !j.isInvoiced);
        
        // We need to check if customer has contract billing - filter those out
        const perJobCustomerJobs = completedJobs.filter(j => {
          // If customer has billingType other than PerJob, exclude from ready to invoice
          return !j.customer?.billingType || j.customer.billingType === 'PerJob';
        });
        
        setJobs(perJobCustomerJobs);
        
        // Load contract customers due for billing
        await loadContractCustomersDue();
      } catch (error) {
      console.error('Error loading data:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load invoices',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPendingPaymentCount = async () => {
    try {
      const response = await api.get('/PendingPayments/count');
      setPendingPaymentCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading pending payment count:', error);
    }
  };

  const loadContractCustomersDue = async () => {
  try {
    const response = await api.get('/Invoices/contract-billing-due');
    setContractCustomersDue(response.data || []);
  } catch (error) {
    console.error('Error loading contract customers:', error);
    setContractCustomersDue([]);
  }
};

const handleGenerateConsolidatedInvoice = async (customerId, periodStart, periodEnd) => {
  try {
    setGeneratingInvoiceFor(customerId);
    
    const response = await api.post(`/Invoices/contract-billing/generate/${customerId}`, {
      periodStart,
      periodEnd
    });
    
    // Show success message
    setConfirmModal({
      isOpen: true,
      title: '✅ Invoice Created!',
      message: `Invoice ${response.data.invoiceNumber} created for ${response.data.customerName}`,
      variant: 'success',
      confirmText: 'OK',
      onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
    });
    
    // Refresh data
    await loadData();
    
  } catch (err) {
    setConfirmModal({
      isOpen: true,
      title: 'Error',
      message: err.response?.data?.error || 'Failed to generate invoice',
      variant: 'danger',
      confirmText: 'OK',
      onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
    });
  } finally {
    setGeneratingInvoiceFor(null);
  }
};

const handleViewContractJobs = (customerId, periodStart, periodEnd) => {
  navigate(`/jobs?customerId=${customerId}`);
};
  const loadBusinessSettings = async () => {
    try {
      let response;
      try {
        response = await api.get('/business/settings');
      } catch (err) {
        response = await api.get('/business/email-settings');
      }
      
      setBusinessSettings({
        sendInvoiceCreated: response.data.sendInvoiceCreated || false,
        sendPaymentReceived: response.data.sendPaymentReceived || false,
        sendPaymentReminders: response.data.sendPaymentReminders || false,
        collectsSalesTax: response.data.collectsSalesTax || false,
        salesTaxRate: response.data.salesTaxRate || 0,
        taxSetupComplete: response.data.taxSetupComplete || false,
        state: response.data.state || null,
        county: response.data.county || null,
      });

      try {
        const stripeResponse = await api.get('/stripe-connect/status');
        setStripeConnectEnabled(stripeResponse.data.isConnected && stripeResponse.data.chargesEnabled);
      } catch (err) {
        setStripeConnectEnabled(false);
      }

      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading business settings:', error);
      setSettingsLoaded(true);
    }
  };

  const getCustomerEmail = (invoice) => {
  // Use customerEmail from API (works for both consolidated and regular invoices)
  if (invoice?.customerEmail) {
    return invoice.customerEmail;
  }
  // Fallback for regular invoices
  if (invoice?.job?.customer?.email) {
    return invoice.job.customer.email;
  }
  return null;
};

  const selectedJobHasEmail = () => {
    if (!formData.jobId) return false;
    const job = jobs.find(j => j.id.toString() === formData.jobId.toString());
    return job?.customer?.email ? true : false;
  };

  const getTaxRateAsDecimal = () => {
    const rate = businessSettings.salesTaxRate || 0;
    return rate > 1 ? rate / 100 : rate;
  };

  const calculateTax = (subtotal) => {
    if (!businessSettings.collectsSalesTax) return '0.00';
    const sub = parseFloat(subtotal) || 0;
    const taxRate = getTaxRateAsDecimal();
    return (sub * taxRate).toFixed(2);
  };

  const calculateSubtotalFromTotal = (total) => {
    if (!businessSettings.collectsSalesTax) return total;
    const totalAmount = parseFloat(total) || 0;
    const taxRate = getTaxRateAsDecimal();
    const subtotal = totalAmount / (1 + taxRate);
    return subtotal.toFixed(2);
  };

  const handleSubtotalChange = (value) => {
    const subTotal = value;
    const taxAmount = calculateTax(subTotal);
    const totalAmount = (parseFloat(subTotal) || 0) + parseFloat(taxAmount);
    
    setFormData({
      ...formData,
      subTotal,
      taxAmount,
      totalAmount: totalAmount.toFixed(2),
    });
    setTaxIncludedInQuote(false);
  };

  const handleTaxIncludedToggle = (checked) => {
    setTaxIncludedInQuote(checked);
    
    if (checked && formData.totalAmount) {
      const total = parseFloat(formData.totalAmount) || 0;
      const subtotal = calculateSubtotalFromTotal(total);
      const taxAmount = (total - parseFloat(subtotal)).toFixed(2);
      
      setFormData({
        ...formData,
        subTotal: subtotal,
        taxAmount: taxAmount,
      });
    } else if (!checked && formData.subTotal) {
      handleSubtotalChange(formData.subTotal);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const selectedJob = jobs.find(j => j.id === parseInt(formData.jobId));
      
      if (editingInvoice) {
        const updateData = {
          id: editingInvoice.id,
          invoiceNumber: formData.invoiceNumber,
          invoiceDate: formData.invoiceDate,
          dueDate: formData.dueDate || null,
          subTotal: parseFloat(formData.subTotal) || 0,
          taxAmount: parseFloat(formData.taxAmount) || 0,
          totalAmount: parseFloat(formData.totalAmount) || 0,
          notes: formData.notes || null,
        };
        await invoicesApi.update(editingInvoice.id, updateData);
        
        setConfirmModal({
          isOpen: true,
          title: '✅ Invoice Updated!',
          message: `Invoice updated successfully.`,
          variant: 'success',
          confirmText: 'OK',
          onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
        });
      } else {
        const createData = {
          jobId: parseInt(formData.jobId),
          invoiceNumber: formData.invoiceNumber || null,
          invoiceDate: formData.invoiceDate,
          dueDate: formData.dueDate || null,
          subTotal: parseFloat(formData.subTotal) || 0,
          taxAmount: parseFloat(formData.taxAmount) || 0,
          totalAmount: parseFloat(formData.totalAmount) || 0,
          notes: formData.notes || null,
          sendInvoiceEmail: selectedJobHasEmail() ? emailOverrides.sendInvoiceEmail : null,
        };
        await invoicesApi.create(createData);
        
        setConfirmModal({
          isOpen: true,
          title: '✅ Invoice Created!',
          message: `Invoice created for ${selectedJob?.customer?.name}. ${
            emailOverrides.sendInvoiceEmail && selectedJob?.customer?.email
              ? `Email will be sent to ${selectedJob?.customer?.email}` 
              : 'No email sent'
          }`,
          variant: 'success',
          confirmText: 'OK',
          onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
        });
      }
      
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving invoice:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save invoice: ' + (error.response?.data?.error || error.message),
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    if (!payingInvoice) return;
    
    try {
      const paymentPayload = {
        amount: parseFloat(paymentData.amount),
        paymentMethod: paymentData.paymentMethod,
        paymentDate: paymentData.paymentDate,
        transactionId: paymentData.transactionId || null,
        notes: paymentData.notes || null,
        sendReceiptEmail: getCustomerEmail(payingInvoice) ? emailOverrides.sendReceiptEmail : null,
      };
      
      await invoicesApi.addPayment(payingInvoice.id, paymentPayload);
      
      setConfirmModal({
        isOpen: true,
        title: '✅ Payment Recorded!',
        message: `Payment of ${formatCurrency(parseFloat(paymentData.amount))} recorded successfully. ${
          emailOverrides.sendReceiptEmail && getCustomerEmail(payingInvoice)
            ? `Receipt will be sent to ${getCustomerEmail(payingInvoice)}` 
            : 'No receipt email sent'
        }`,
        variant: 'success',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
      
      await loadData();
      handleClosePaymentModal();
    } catch (error) {
      console.error('Error recording payment:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to record payment: ' + (error.response?.data?.error || error.message),
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    }
  };

  const handleOpenPaymentModal = (invoice) => {
    setPayingInvoice(invoice);
    setPaymentData({
      amount: invoice.amountDue?.toString() || invoice.totalAmount?.toString() || '',
      paymentMethod: 'Cash',
      paymentDate: new Date().toISOString().split('T')[0],
      transactionId: '',
      notes: '',
    });
    setEmailOverrides(prev => ({
      ...prev,
      sendReceiptEmail: businessSettings.sendPaymentReceived,
    }));
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPayingInvoice(null);
    setPaymentData({
      amount: '',
      paymentMethod: 'Cash',
      paymentDate: new Date().toISOString().split('T')[0],
      transactionId: '',
      notes: '',
    });
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      jobId: invoice.jobId,
      invoiceNumber: invoice.invoiceNumber || '',
      invoiceDate: invoice.invoiceDate?.split('T')[0] || '',
      dueDate: invoice.dueDate?.split('T')[0] || '',
      subTotal: invoice.subTotal?.toString() || '',
      taxAmount: invoice.taxAmount?.toString() || '',
      totalAmount: invoice.totalAmount?.toString() || '',
      notes: invoice.notes || '',
    });
    setTaxIncludedInQuote(false);
    setShowModal(true);
  };

  const handleDelete = (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    
    if (invoice?.payments && invoice.payments.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: 'Cannot Delete',
        message: 'This invoice has payments recorded. You cannot delete it.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete Invoice',
      message: `Are you sure you want to delete invoice ${invoice?.invoiceNumber || `#${invoiceId}`}?`,
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await invoicesApi.delete(invoiceId);
          await loadData();
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error deleting invoice:', error);
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: error.response?.data?.error || 'Failed to delete invoice',
            variant: 'danger',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
          });
        }
      },
    });
  };

  const handleSendInvoice = async (invoice) => {
    if (!getCustomerEmail(invoice)) {
      setConfirmModal({
        isOpen: true,
        title: 'No Email',
        message: 'This customer does not have an email address on file.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
      return;
    }

    const isResend = invoice.emailSent;

    if (isResend) {
      setConfirmModal({
        isOpen: true,
        title: 'Resend Invoice?',
        message: `This invoice was sent on ${formatDate(invoice.emailSentAt)}. Send again?`,
        variant: 'warning',
        confirmText: 'Resend',
        cancelText: 'Cancel',
        onConfirm: async () => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          await sendInvoiceEmail(invoice, true);
        },
      });
      return;
    }

    await sendInvoiceEmail(invoice, false);
  };

  const sendInvoiceEmail = async (invoice, isResend) => {
    try {
      setSendingEmail(invoice.id);
      await invoicesApi.send(invoice.id);
      
      setConfirmModal({
        isOpen: true,
        title: isResend ? 'Invoice Resent!' : 'Invoice Sent!',
        message: `Invoice ${isResend ? 'resent' : 'sent'} to ${getCustomerEmail(invoice)}`,
        variant: 'success',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
      
      await loadData();
    } catch (error) {
      console.error('Error sending invoice:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to send invoice email',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    } finally {
      setSendingEmail(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingInvoice(null);
    setTaxIncludedInQuote(false);
    setFormData({
      jobId: '',
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      subTotal: '',
      taxAmount: '',
      totalAmount: '',
      notes: '',
    });
    setEmailOverrides({
      sendInvoiceEmail: businessSettings.sendInvoiceCreated,
      sendReceiptEmail: businessSettings.sendPaymentReceived,
    });
  };

  const handleOpenNewInvoiceModal = () => {
    setEmailOverrides({
      sendInvoiceEmail: businessSettings.sendInvoiceCreated,
      sendReceiptEmail: businessSettings.sendPaymentReceived,
    });
    setTaxIncludedInQuote(false);
    setShowModal(true);
  };

  const handleQuickInvoice = (job) => {
    const subtotal = job.finalAmount || job.quoteAmount || 0;
    const taxAmount = calculateTax(subtotal);
    const totalAmount = subtotal + parseFloat(taxAmount);
    
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 14);
    
    setFormData({
      jobId: job.id.toString(),
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      subTotal: subtotal.toString(),
      taxAmount,
      totalAmount: totalAmount.toFixed(2),
      notes: '',
    });
    
    setEmailOverrides({
      sendInvoiceEmail: businessSettings.sendInvoiceCreated && job.customer?.email ? true : false,
      sendReceiptEmail: businessSettings.sendPaymentReceived,
    });
    
    setTaxIncludedInQuote(false);
    setShowModal(true);
  };

  const formatEmailSentTime = (date) => {
    if (!date) return '';
    const sentDate = new Date(date);
    const now = new Date();
    const diffMs = now - sentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  if (loading && invoices.length === 0) return <Loading fullScreen />;

  const totalRevenue = invoices
    .filter(i => i.status === 'Paid')
    .reduce((sum, i) => sum + (i.totalAmount || 0), 0);
  
  const pendingAmount = invoices
    .filter(i => i.status === 'Pending' || i.status === 'Unpaid' || i.status === 'Partial')
    .reduce((sum, i) => sum + (i.amountDue || i.totalAmount || 0), 0);

  const overdueAmount = invoices
    .filter(i => i.status === 'Overdue')
    .reduce((sum, i) => sum + (i.amountDue || i.totalAmount || 0), 0);

  const invoiceCount = {
    total: invoices.length,
    paid: invoices.filter(i => i.status === 'Paid').length,
    pending: invoices.filter(i => ['Pending', 'Unpaid', 'Partial'].includes(i.status)).length,
    overdue: invoices.filter(i => i.status === 'Overdue').length,
  };

  const anyEmailsEnabled = businessSettings.sendInvoiceCreated || businessSettings.sendPaymentReceived;

  const selectedJob = formData.jobId 
    ? jobs.find(j => j.id.toString() === formData.jobId.toString())
    : null;
  const newInvoiceCustomerHasEmail = selectedJob?.customer?.email ? true : false;

  const taxRatePercent = businessSettings.salesTaxRate > 1 
    ? businessSettings.salesTaxRate.toFixed(2)
    : (businessSettings.salesTaxRate * 100).toFixed(2);

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-600 mt-1">{invoices.length} total invoices</p>
          </div>
          <button
            onClick={handleOpenNewInvoiceModal}
            className="btn btn-primary flex items-center gap-2 justify-center"
          >
            <Plus size={20} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <div className="rounded-xl p-4 lg:p-5" style={{background: 'linear-gradient(135deg, #052e16, #14532d)'}}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-300 text-xs lg:text-sm font-medium mb-1">Total Revenue</p>
              <p className="text-xl lg:text-2xl font-bold text-green-100 tracking-tight">
                {formatCurrency(totalRevenue)}
              </p>
              <p className="text-xs text-green-400 mt-1">{invoiceCount.paid} paid</p>
            </div>
            <TrendingUp className="text-green-400 hidden sm:block" size={24} />
          </div>
        </div>

        <div className="rounded-xl p-4 lg:p-5" style={{background: 'linear-gradient(135deg, #422006, #78350f)'}}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-300 text-xs lg:text-sm font-medium mb-1">Unpaid</p>
              <p className="text-xl lg:text-2xl font-bold text-amber-100 tracking-tight">
                {formatCurrency(pendingAmount)}
              </p>
              <p className="text-xs text-amber-400 mt-1">{invoiceCount.pending} invoices</p>
            </div>
            <Clock className="text-amber-400 hidden sm:block" size={24} />
          </div>
        </div>

        <div className="rounded-xl p-4 lg:p-5" style={{background: 'linear-gradient(135deg, #450a0a, #7f1d1d)'}}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-300 text-xs lg:text-sm font-medium mb-1">Overdue</p>
              <p className="text-xl lg:text-2xl font-bold text-red-100 tracking-tight">
                {formatCurrency(overdueAmount)}
              </p>
              <p className="text-xs text-red-400 mt-1">{invoiceCount.overdue} invoices</p>
            </div>
            <TrendingDown className="text-red-400 hidden sm:block" size={24} />
          </div>
        </div>

        <div className="rounded-xl p-4 lg:p-5" style={{background: 'linear-gradient(135deg, #0f172a, #1e3a8a)'}}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300 text-xs lg:text-sm font-medium mb-1">Collection Rate</p>
              <p className="text-xl lg:text-2xl font-bold text-blue-100 tracking-tight">
                {invoiceCount.total > 0 
                  ? Math.round((invoiceCount.paid / invoiceCount.total) * 100) 
                  : 0}%
              </p>
              <p className="text-xs text-blue-400 mt-1">{invoiceCount.total} total</p>
            </div>
            <DollarSign className="text-blue-400 hidden sm:block" size={24} />
          </div>
        </div>
      </div>

      {/* Info Legends */}
      <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
        {anyEmailsEnabled && settingsLoaded && (
          <div className="flex items-center gap-2">
            <Mail size={12} className="text-blue-500" />
            <span>Auto-emails on</span>
            {isOwner && (
              <a href="/settings" className="text-primary-600 hover:underline">(Manage)</a>
            )}
          </div>
        )}
        {businessSettings.collectsSalesTax && settingsLoaded && (
          <div className="flex items-center gap-2">
            <DollarSign size={12} className="text-green-500" />
            <span>Tax: {taxRatePercent}%</span>
          </div>
        )}
        {stripeConnectEnabled && settingsLoaded && (
          <div className="flex items-center gap-2">
            <CreditCard size={12} className="text-purple-500" />
            <span>Online payments</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'invoices'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Invoices
        </button>
        {isOwner && (
          <button
            onClick={() => setActiveTab('contracts')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'contracts'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Contract Billing
            {contractCustomersDue.filter(c => c.previousPeriod?.isReady).length > 0 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                {contractCustomersDue.filter(c => c.previousPeriod?.isReady).length}
              </span>
            )}
          </button>
        )}
        {isOwner && (
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'payments'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Payments
          </button>
        )}
        <button
          onClick={() => setActiveTab('reconcile')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'reconcile'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Reconcile Payments
          {pendingPaymentCount > 0 && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
              {pendingPaymentCount}
            </span>
          )}
        </button>
      </div>

      {/* Payments Tab Content */}
      {activeTab === 'payments' && isOwner && (
        <PaymentsTab onOpenInvoice={handleInvoiceClick} />
      )}

      {/* Reconcile Tab Content */}
      {activeTab === 'reconcile' && (
        <PaymentReconciliation 
          onPendingCountChange={(count) => setPendingPaymentCount(count)}
        />
      )}

      {/* Contract Billing Tab Content */}
      {activeTab === 'contracts' && (
        <ContractBillingDue
          customers={contractCustomersDue}
          onGenerateInvoice={handleGenerateConsolidatedInvoice}
          generatingFor={generatingInvoiceFor}
          onViewJobs={handleViewContractJobs}
        />
      )}

      {/* Invoices Tab Content */}
      {activeTab === 'invoices' && (
      <>

      {/* Ready to Invoice Section */}
      {jobs.length > 0 && (
        <div className="mb-6">
          <div className="rounded-xl p-4 lg:p-6" style={{background: 'linear-gradient(135deg, #052e16, #14532d)'}}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center">
                <FileText className="text-white" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-green-100">Ready to Invoice</h2>
                <p className="text-sm text-green-300">
                  {jobs.length} completed {jobs.length === 1 ? 'job' : 'jobs'} waiting
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{job.customer?.name || 'Unknown'}</h3>
                      <p className="text-sm text-gray-600 truncate">{job.address}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{formatDate(job.completedDate)}</span>
                        <span className="text-green-600 font-medium">
                          {formatCurrency(job.finalAmount || job.quoteAmount)}
                        </span>
                        {!job.customer?.email && (
                          <span className="text-amber-600">(no email)</span>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleQuickInvoice(job)}
                      className="btn btn-primary whitespace-nowrap"
                    >
                      Create Invoice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by invoice # or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {['All', 'Unpaid', 'Paid', 'Overdue'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status.toLowerCase())}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === status.toLowerCase()
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status}
              {status !== 'All' && (
                <span className="ml-1.5 text-xs opacity-75">
                  ({status === 'Paid' ? invoiceCount.paid 
                    : status === 'Overdue' ? invoiceCount.overdue 
                    : status === 'Unpaid' ? invoiceCount.pending
                    : ''})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="card text-center py-12">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'all' ? 'No invoices found' : 'No invoices yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters' 
              : jobs.length > 0
                ? 'Create invoices from the completed jobs above'
                : 'Complete a job first, then create an invoice'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredInvoices.map((invoice) => (
  <InvoiceListItem
    key={invoice.id}
    invoice={invoice}
    onClick={() => handleInvoiceClick(invoice)}
  />
))}
        </div>
      )}

      </>
      )}

      {/* Add/Edit Invoice Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingInvoice && (
            <div>
              <label className="label">Completed Job *</label>
              <select
                required
                value={formData.jobId}
                onChange={(e) => {
                  const selectedJob = jobs.find(j => j.id === parseInt(e.target.value));
                  const subtotal = selectedJob?.finalAmount || selectedJob?.quoteAmount || 0;
                  const taxAmount = calculateTax(subtotal);
                  const totalAmount = subtotal + parseFloat(taxAmount);
                  
                  const dueDate = new Date();
                  dueDate.setDate(dueDate.getDate() + 14);
                  
                  setFormData({ 
                    ...formData, 
                    jobId: e.target.value,
                    subTotal: subtotal.toString(),
                    taxAmount,
                    totalAmount: totalAmount.toFixed(2),
                    dueDate: dueDate.toISOString().split('T')[0],
                  });
                  
                  setEmailOverrides(prev => ({
                    ...prev,
                    sendInvoiceEmail: businessSettings.sendInvoiceCreated,
                  }));
                  setTaxIncludedInQuote(false);
                }}
                className="input"
              >
                <option value="">Select a completed job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.customer?.name || 'Unknown'} - {job.address} - {formatCurrency(job.finalAmount || job.quoteAmount)}
                    {!job.customer?.email && ' (no email)'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {editingInvoice && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Customer:</span> {editingInvoice.customerName || editingInvoice.job?.customer?.name || 'Unknown'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Job:</span> {editingInvoice.isConsolidated 
                  ? `${editingInvoice.jobCount || 0} jobs (${editingInvoice.billingPeriodStart ? formatDate(editingInvoice.billingPeriodStart) : ''} - ${editingInvoice.billingPeriodEnd ? formatDate(editingInvoice.billingPeriodEnd) : ''})` 
                  : (editingInvoice.job?.address || 'N/A')}
              </p>
              {editingInvoice.isConsolidated && (
                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                  <Users size={12} />
                  Consolidated Invoice
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                className="input"
                placeholder="Auto-generated"
              />
            </div>
            <div>
              <label className="label">Invoice Date *</label>
              <input
                type="date"
                required
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="label">Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="input"
            />
          </div>

          {businessSettings.collectsSalesTax ? (
            <>
              <div className="bg-gray-50 rounded-lg p-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={taxIncludedInQuote}
                    onChange={(e) => handleTaxIncludedToggle(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Quote includes tax</span>
                    <p className="text-xs text-gray-500">Check if you quoted a flat rate with tax included</p>
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">{taxIncludedInQuote ? 'Subtotal' : 'Subtotal *'}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      required={!taxIncludedInQuote}
                      disabled={taxIncludedInQuote}
                      value={formData.subTotal}
                      onChange={(e) => handleSubtotalChange(e.target.value)}
                      className={`input pl-7 ${taxIncludedInQuote ? 'bg-gray-50' : ''}`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Tax ({taxRatePercent}%)</label>
                  <input
                    type="text"
                    value={formatCurrency(formData.taxAmount)}
                    disabled
                    className="input bg-gray-50"
                  />
                </div>
                <div>
                  <label className="label">{taxIncludedInQuote ? 'Total *' : 'Total'}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      required={taxIncludedInQuote}
                      disabled={!taxIncludedInQuote}
                      value={formData.totalAmount}
                      onChange={(e) => {
                        const total = e.target.value;
                        const subtotal = calculateSubtotalFromTotal(total);
                        const taxAmount = (parseFloat(total) - parseFloat(subtotal)).toFixed(2);
                        setFormData({
                          ...formData,
                          totalAmount: total,
                          subTotal: subtotal,
                          taxAmount: taxAmount,
                        });
                      }}
                      className={`input pl-7 font-bold ${taxIncludedInQuote ? 'border-primary-300 bg-primary-50' : 'bg-gray-50'}`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="label">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.totalAmount}
                  onChange={(e) => {
                    const amount = e.target.value;
                    setFormData({
                      ...formData,
                      subTotal: amount,
                      taxAmount: '0',
                      totalAmount: amount,
                    });
                  }}
                  className="input pl-7 text-lg font-bold"
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              rows="2"
              placeholder="Payment terms, special instructions..."
            />
          </div>

          {!editingInvoice && newInvoiceCustomerHasEmail && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailOverrides.sendInvoiceEmail}
                  onChange={(e) => setEmailOverrides({ ...emailOverrides, sendInvoiceEmail: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-primary-600 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <Mail size={14} className="text-blue-600" />
                    Send invoice email to customer
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {businessSettings.sendInvoiceCreated ? 'Enabled by default' : 'Disabled by default'}
                  </p>
                </div>
              </label>
            </div>
          )}

          {!editingInvoice && formData.jobId && !newInvoiceCustomerHasEmail && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">No customer email</p>
                  <p className="text-xs text-amber-700">No invoice email will be sent.</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
            </button>
            <button type="button" onClick={handleCloseModal} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        title="Record Payment"
      >
        <form onSubmit={handleRecordPayment} className="space-y-4">
          {payingInvoice && (
  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
    <p className="text-sm">
      <span className="font-medium">{payingInvoice.invoiceNumber || `INV-${payingInvoice.id}`}</span>
      <span className="text-gray-500"> • {payingInvoice.customerName || payingInvoice.job?.customer?.name || 'Unknown'}</span>
      {payingInvoice.isConsolidated && (
        <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
          {payingInvoice.jobCount} jobs
        </span>
      )}
    </p>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Invoice Total:</span>
      <span className="font-medium">{formatCurrency(payingInvoice.totalAmount)}</span>
    </div>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Amount Due:</span>
      <span className="font-bold text-primary-600">{formatCurrency(payingInvoice.amountDue || payingInvoice.totalAmount)}</span>
    </div>
  </div>
)}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="input pl-7"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="label">Payment Date *</label>
              <input
                type="date"
                required
                value={paymentData.paymentDate}
                onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                className="input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Payment Method *</label>
              <select
                required
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                className="input"
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Venmo">Venmo</option>
                <option value="Zelle">Zelle</option>
                <option value="PayPal">PayPal</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Reference #</label>
              <input
                type="text"
                value={paymentData.transactionId}
                onChange={(e) => setPaymentData({ ...paymentData, transactionId: e.target.value })}
                className="input"
                placeholder="Check #, confirmation..."
              />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={paymentData.notes}
              onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              className="input"
              rows="2"
              placeholder="Optional notes..."
            />
          </div>

          {payingInvoice && getCustomerEmail(payingInvoice) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailOverrides.sendReceiptEmail}
                  onChange={(e) => setEmailOverrides({ ...emailOverrides, sendReceiptEmail: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-primary-600 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <Mail size={14} className="text-blue-600" />
                    Send payment receipt to customer
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {businessSettings.sendPaymentReceived ? 'Enabled by default' : 'Disabled by default'}
                  </p>
                </div>
              </label>
            </div>
          )}

          {payingInvoice && !getCustomerEmail(payingInvoice) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">No customer email</p>
                  <p className="text-xs text-amber-700">No receipt email will be sent.</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              Record Payment
            </button>
            <button type="button" onClick={handleClosePaymentModal} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
      />

      <InvoiceDetailDrawer
        invoice={selectedInvoice}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedInvoice(null);
        }}
        onEdit={(invoice) => {
          setDrawerOpen(false);
          handleEdit(invoice);
        }}
        onDelete={(invoiceId) => {
          setDrawerOpen(false);
          handleDelete(invoiceId);
        }}
        onRecordPayment={(invoice) => {
          setDrawerOpen(false);
          handleOpenPaymentModal(invoice);
        }}
        onRefresh={loadData}
        stripeConnectEnabled={stripeConnectEnabled}
        businessSettings={businessSettings}
      />
    </div>
  );
};

export default Invoices;